import { PackMotion } from './lowa-pack-motion.js';
import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * Arrival video and navigation enhancement; native links remain the fallback.
 * @typedef {object} HeaderRefs
 * @property {HTMLElement} card
 * @property {HTMLElement} range
 * @property {HTMLElement} spacer
 * @property {HTMLVideoElement} [video]
 * @property {HTMLDialogElement} menu
 * @property {HTMLButtonElement} menuButton
 * @extends {Component<HeaderRefs>}
 */
class LowaHeader extends Component {
  requiredRefs = ['card', 'range', 'spacer', 'menu', 'menuButton'];
  #controller;
  #resizeObserver;
  #intersectionObserver;
  #frame = 0;
  #distance = 1;
  #viewportWidth = 0;
  #viewportHeight = 0;
  #collapseDistance = 0;
  #progress = 0;
  #playPending = false;
  #generation = 0;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #mobile = window.matchMedia('(max-width: 749px)');

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #cleanup() {
    this.#generation++;
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.refs.video?.pause();
    this.closeMenu();
    this.#playPending = false;
  }

  #initialize() {
    this.#cleanup();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    window.addEventListener('resize', this.#measure, { passive: true, signal });
    window.addEventListener('scroll', () => {
      if (this.#mobile.matches || !CSS.supports('animation-timeline: scroll()')) this.#schedule();
    }, { passive: true, signal });
    window.addEventListener('pageshow', this.#measure, { signal });
    document.addEventListener('visibilitychange', this.#syncVideo, { signal });
    this.#reducedMotion.addEventListener('change', this.#schedule, { signal });
    this.#mobile.addEventListener('change', () => { if (!this.#mobile.matches) this.closeMenu(); }, { signal });
    this.refs.menu.addEventListener('cancel', this.#onMenuCancel, { signal });
    this.refs.menu.addEventListener('close', this.#onMenuClose, { signal });
    this.#resizeObserver = new ResizeObserver(this.#measure);
    this.#resizeObserver.observe(this.refs.range);
    this.#intersectionObserver = new IntersectionObserver(this.#schedule, {
      threshold: Array.from({ length: 201 }, (_, index) => index / 200),
    });
    this.#intersectionObserver.observe(this.refs.range);
    this.#measure();
  }

  #measure = () => {
    if (this.#mobile.matches) {
      // Browser bars can resize an iPhone viewport mid-scroll. Keep the same
      // geometry until the width changes (rotation or a real layout resize).
      if (this.#viewportWidth !== window.innerWidth) {
        this.style.removeProperty('--lowa-viewport-height');
        this.style.removeProperty('--lowa-collapse-distance');
        this.#viewportHeight = this.refs.spacer.getBoundingClientRect().height;
        this.#collapseDistance = this.refs.range.getBoundingClientRect().height;
        this.#viewportWidth = window.innerWidth;
      }
      this.style.setProperty('--lowa-viewport-height', `${this.#viewportHeight}px`);
      this.style.setProperty('--lowa-collapse-distance', `${this.#collapseDistance}px`);
    } else {
      this.style.removeProperty('--lowa-viewport-height');
      this.style.removeProperty('--lowa-collapse-distance');
      this.#viewportWidth = 0;
    }
    this.#distance = Math.max(1, this.refs.range.getBoundingClientRect().height);
    this.#schedule();
  };

  #schedule = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#progress = Math.max(0, Math.min(1, window.scrollY / this.#distance));
      const reduced = this.#reducedMotion.matches;
      const fallback = this.#mobile.matches || !CSS.supports('animation-timeline: scroll()');
      this.classList.toggle('is-collapsed', reduced && this.#progress >= .4);
      if (fallback && !reduced && this.dataset.home === 'true') this.#renderFallback();
      else this.#clearFallback();
      this.#syncVideo();
    });
  };

  #clearFallback() {
    this.style.removeProperty('--lowa-fade');
    for (const element of this.querySelectorAll('.lowa-arrival, .lowa-arrival__brand')) {
      for (const property of ['height', 'top', 'width', 'inset-inline-start', 'transform', 'opacity', 'visibility', 'border-radius']) {
        element.style.removeProperty(property);
      }
    }
  }

  #renderFallback() {
    const p = this.#progress;
    const styles = getComputedStyle(this);
    const inset = parseFloat(styles.getPropertyValue('--lowa-inset'));
    const compact = parseFloat(styles.getPropertyValue('--lowa-header-height'));
    const full = this.refs.spacer.getBoundingClientRect().height - inset * 2;
    this.refs.card.style.height = `${full - (full - compact) * p}px`;
    this.refs.card.style.top = `${inset + (12 - inset) * p}px`;
    this.refs.card.style.borderRadius = `${16 - 4 * p}px`;
    this.style.setProperty('--lowa-fade', String(Math.max(0, Math.min(1, (p - .4) / .6))));
    const brand = this.querySelector('.lowa-arrival__brand');
    if (!(brand instanceof HTMLElement)) return;
    const small = parseFloat(styles.getPropertyValue('--lowa-small-logo'));
    const left = parseFloat(styles.getPropertyValue('--lowa-logo-left'));
    const width = this.refs.card.clientWidth;
    const big = this.#mobile.matches ? width * .8 : Math.min(width * .56, 720);
    const start = (width - big) / 2;
    brand.style.width = `${big + (small - big) * p}px`;
    brand.style.insetInlineStart = `${start + (left - start) * p}px`;
    brand.style.transform = 'translateY(-50%)';
  }

  #shouldPlay() {
    return this.isConnected && !document.hidden && this.#progress < 1 && !this.#reducedMotion.matches;
  }

  #syncVideo = async () => {
    const video = this.refs.video;
    if (!video) return;
    // Normal speed for 70% of the collapse, then a smooth ease into rest.
    const t = Math.max(0, Math.min(1, (this.#progress - .7) / .3));
    const ease = t * t * t * (t * (t * 6 - 15) + 10);
    video.playbackRate = 1 - .9 * ease;
    if (!this.#shouldPlay()) {
      video.pause();
    } else if (video.paused && !this.#playPending) {
      const generation = this.#generation;
      this.#playPending = true;
      try {
        await video.play();
        if (generation !== this.#generation || !this.#shouldPlay()) video.pause();
      } catch {
        // The poster stays visible if autoplay is blocked.
      } finally {
        if (generation === this.#generation) this.#playPending = false;
      }
    }
  };

  /** @param {MouseEvent} event - Menu control click. */
  openMenu(event) {
    event.preventDefault();
    if (this.refs.menu.open) return;
    lockScroll(this.refs.menu);
    this.refs.menu.showModal();
    this.refs.menuButton.setAttribute('aria-expanded', 'true');
  }

  /** Close the native menu; anchors keep their default navigation. */
  closeMenu() {
    if (!this.refs.menu?.open) return;
    unlockScroll(this.refs.menu);
    this.refs.menu.close();
    this.refs.menuButton?.setAttribute('aria-expanded', 'false');
  }

  #onMenuCancel = (event) => { event.preventDefault(); this.closeMenu(); };
  #onMenuClose = () => { unlockScroll(this.refs.menu); this.refs.menuButton.setAttribute('aria-expanded', 'false'); };

  /** @param {MouseEvent} event - Native cart link, enhanced only when its drawer is ready. */
  openCart(event) {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    this.closeMenu();
    drawer.open();
  }

  /** @param {MouseEvent} event - Native search link, enhanced only when the modal is ready. */
  openSearch(event) {
    const search = document.getElementById('search-modal');
    if (!search || typeof search.showDialog !== 'function') return;
    event.preventDefault();
    this.closeMenu();
    search.showDialog();
  }
}

/**
 * Selects a complete native product form for each pack, preserving all purchase contracts.
 * @typedef {object} PackRefs
 * @property {HTMLFieldSetElement} [choices]
 * @extends {Component<PackRefs>}
 */
class LowaPacks extends Component {
  #motion;
  #controller;
  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#motion?.destroy();
    super.disconnectedCallback();
  }

  #initialize() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#motion?.destroy();
    this.#motion = new PackMotion(this);
    if (!this.refs.choices) return;
    this.refs.choices.hidden = false;
    window.addEventListener('hashchange', this.#selectLinkedPack, { signal: this.#controller.signal });
    if (this.#selectLinkedPack()) return;
    this.#showPack(this.querySelector('input[type="radio"]:checked')?.value || this.dataset.selectedPack, false);
  }

  #selectLinkedPack = () => {
    for (const choice of this.querySelectorAll('input[data-product-id]')) {
      if (!(choice instanceof HTMLInputElement) || window.location.hash !== `#pack-${choice.dataset.productId}`) continue;
      choice.checked = true;
      this.#showPack(choice.value, false);
      return true;
    }
    return false;
  };

  #showPack(id, animate = true) {
    this.#motion?.show(id, animate);
    for (const form of this.querySelectorAll('[data-pack]')) {
      if (!(form instanceof HTMLElement)) continue;
      form.hidden = form.dataset.pack !== id;
      const title = form.querySelector('.lowa-pack-title');
      if (title instanceof HTMLElement) title.hidden = true;
    }
  }

  /** @param {Event} event - Pack radio change. */
  selectPack(event) {
    if (!(event.target instanceof HTMLInputElement) || !event.target.checked) return;
    this.#showPack(event.target.value);
    if (event.target.dataset.productId) {
      const url = new URL(window.location.href);
      url.hash = `pack-${event.target.dataset.productId}`;
      history.replaceState(history.state, '', url);
    }
  }
}

/**
 * Sends old simple-pack URLs to the shared beer page; the native link works without JavaScript.
 * @extends {Component<{link: HTMLAnchorElement}>}
 */
class LowaBeerRedirect extends Component {
  requiredRefs = ['link'];

  connectedCallback() {
    super.connectedCallback();
    if (!window.Shopify?.designMode) window.location.replace(this.refs.link.href);
  }
}

if (!customElements.get('lowa-header-component')) customElements.define('lowa-header-component', LowaHeader);
if (!customElements.get('lowa-packs-component')) customElements.define('lowa-packs-component', LowaPacks);
if (!customElements.get('lowa-beer-redirect')) customElements.define('lowa-beer-redirect', LowaBeerRedirect);
